// src/lib/albergue/aptitudAlbergue.ts

/**
 * ALBTIPO (29-sep-2026) · ¿sirve este elemento como albergue en una emergencia?
 *
 * Función pura sobre la tipología canónica (`mapTipologia`, el mismo resolutor
 * que ven P1, P2 y P4) **y el nombre**: medido sobre el corpus (2 135 filas),
 * la tipología sola no decide —hoteles, casas rurales y salones de usos
 * múltiples caen en GENERICO; CULTURAL mezcla bibliotecas con castillos;
 * DEPORTIVO mezcla pabellones con pistas— (`.ptel/p0_albtipo_20260929/AUDIT_ALBTIPO.md`).
 *
 * DOP selladas por Luis («adelante», 29-sep, 19:19):
 *  - DOP-1: dos estados con motivo, *apto / no apto*; los centros educativos
 *    son aptos con aviso («como en el PTEAnd»).
 *  - DOP-4: sin señal en tipología ni nombre → *sin clasificar*, nunca apto ni
 *    no apto por defecto.
 *  - DOP-5: la tipología técnica manda sobre el nombre; al aire libre no apto
 *    (la capacidad de albergue se calcula solo con área cerrada); servicios
 *    operativos no aptos; iglesias sin clasificar; patrimonio no apto;
 *    polideportivos y pabellones aptos; residencias aptas con aviso.
 *  - DOP-6: si el plan declara aforo y la regla no da apto, manda la regla y se
 *    avisa de que el plan lo propone como albergue.
 *
 * La capacidad no es de esta función (`ALBCAP`).
 *
 * @module lib/albergue/aptitudAlbergue
 */

import type { TipologiaPTEL } from '@/types/tipologia';

export type EstadoAlbergue = 'apto' | 'no_apto' | 'sin_clasificar';

export interface AptitudAlbergue {
  estado: EstadoAlbergue;
  /** Motivo principal del estado, en minúscula y sin punto final. */
  motivo: string;
  /** Avisos que no cambian el estado (escolarización, residentes, plan). */
  avisos: string[];
}

/** Rótulo del estado para pantalla y exportación. */
export const ETIQUETA_ESTADO_ALBERGUE: Record<EstadoAlbergue, string> = {
  apto: 'Apto',
  no_apto: 'No apto',
  sin_clasificar: 'Sin clasificar',
};

export const MOTIVOS_ALBERGUE = {
  tecnica: 'infraestructura técnica',
  patrimonio: 'patrimonio o elemento no habitable',
  aireLibre: 'al aire libre: la capacidad de albergue solo cuenta área cerrada',
  educativo: 'centro educativo',
  residencia: 'residencia',
  cubierto: 'edificio cubierto de uso colectivo',
  operativo: 'servicio operativo durante la emergencia',
  cultural: 'patrimonio cultural sin uso de centro cultural',
  religioso: 'local religioso: ningún plan del corpus lo propone',
  sinSubtipo: 'la tipología no basta y el nombre no lo aclara',
  sinSenal: 'sin datos para decidir',
} as const;

export const AVISOS_ALBERGUE = {
  escolarizacion: 'interrumpe la escolarización',
  residentes: 'ocupada por residentes',
  planPropone: (aforo: number) => `el plan le declara aforo ${aforo}: lo propone como albergue`,
} as const;

const TECNICAS: ReadonlySet<string> = new Set([
  'HIDRAULICO', 'ENERGIA', 'TELECOM', 'VIAL', 'COMBUSTIBLE', 'RSU', 'INDUSTRIAL', 'PUERTO',
]);
const OPERATIVAS: ReadonlySet<string> = new Set(['SANITARIO', 'POLICIAL', 'BOMBEROS', 'EMERGENCIAS']);
const SIN_SUBTIPO: ReadonlySet<string> = new Set(['DEPORTIVO', 'SOCIAL', 'MUNICIPAL']);

/** Palabra o frase al inicio de palabra (el texto llega sin tildes y en minúscula). */
const alguna = (formas: readonly string[]): RegExp =>
  new RegExp(`(?:^|[^a-z0-9])(?:${formas.join('|')})`);

const RE_PATRIMONIO_CULTO = alguna(['romano', 'romana', 'bic\\b', 'arabe', 'medieval']);
const RE_EDUCATIVO = alguna([
  'colegio', 'c\\.?\\s?e\\.?\\s?i\\.?\\s?p\\b', 'cpr\\b', 'c\\.\\s?p\\.\\s?r\\b', 'i\\.?\\s?e\\.?\\s?s\\b',
  'instituto', 'escuela', 'guarderia', 'infantil',
]);
const RE_RESIDENCIA = alguna(['residencia']);
const RE_CUBIERTO = alguna([
  'albergue', 'hotel', 'hostal', 'posada', 'alojamiento', 'casas? rural',
  'pabellon', 'polidep', 'gimnasio', 'salon', 'sala de exposicion',
  'centro cultural', 'casa de la cultura', 'biblioteca', 'teatro', 'espacio escenico',
  'auditorio', 'centro civico', 'hogar', 'guadalinfo',
  'ayuntamiento', '(?:casa|edificio) consistorial', 'naves? (?:municipal|ferial|de servicios)',
  '(?:servicios|usos) multiples', 'centro del profesorado', 'diocesano', 'centro de dia',
  'estancias? diurna', 'participacion activa', 'centro social',
]);
const RE_AIRE_LIBRE = alguna([
  'pista', 'campo de fut', 'campo fut', 'campo municipal', 'estadio', 'piscina', 'parque',
  'plaza', 'paraje', 'zona\\b', 'area recreativa', 'recinto ferial', 'punto de encuentro',
]);
const RE_NO_HABITABLE = alguna([
  'yacimiento', 'castillo', 'torre', 'atalaya', 'molino', 'acequia', 'fuente', 'manantial',
  'cementerio', 'cueva', 'mirador', 'ruinas', 'poblado', 'necropolis', 'puente', 'embalse',
  'balsa', 'deposito', 'muralla', 'alcazaba',
]);

const RE_CEMENTERIO = alguna(['cementerio']);

/** Minúsculas y sin diacríticos, para que «Pabellón» y «PABELLON» casen igual. */
function normalizar(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

function decidir(tipologia: TipologiaPTEL | string, n: string): AptitudAlbergue {
  const r = (estado: EstadoAlbergue, motivo: string, avisos: string[] = []): AptitudAlbergue =>
    ({ estado, motivo, avisos });

  if (TECNICAS.has(tipologia)) return r('no_apto', MOTIVOS_ALBERGUE.tecnica);
  if (RE_PATRIMONIO_CULTO.test(n)) return r('no_apto', MOTIVOS_ALBERGUE.patrimonio);
  if (RE_RESIDENCIA.test(n)) return r('apto', MOTIVOS_ALBERGUE.residencia, [AVISOS_ALBERGUE.residentes]);
  if (tipologia === 'EDUCATIVO' || RE_EDUCATIVO.test(n)) {
    return r('apto', MOTIVOS_ALBERGUE.educativo, [AVISOS_ALBERGUE.escolarizacion]);
  }
  if (RE_CUBIERTO.test(n)) return r('apto', MOTIVOS_ALBERGUE.cubierto);
  if (OPERATIVAS.has(tipologia)) return r('no_apto', MOTIVOS_ALBERGUE.operativo);
  if (RE_AIRE_LIBRE.test(n)) return r('no_apto', MOTIVOS_ALBERGUE.aireLibre);
  // Una ermita llamada «Fuente Grande» es un local religioso, no una fuente;
  // el cementerio sí es no habitable aunque venga como RELIGIOSO.
  if (tipologia === 'RELIGIOSO' && !RE_CEMENTERIO.test(n)) {
    return r('sin_clasificar', MOTIVOS_ALBERGUE.religioso);
  }
  if (RE_NO_HABITABLE.test(n)) return r('no_apto', MOTIVOS_ALBERGUE.patrimonio);
  if (tipologia === 'CULTURAL') return r('no_apto', MOTIVOS_ALBERGUE.cultural);
  if (SIN_SUBTIPO.has(tipologia)) return r('sin_clasificar', MOTIVOS_ALBERGUE.sinSubtipo);
  return r('sin_clasificar', MOTIVOS_ALBERGUE.sinSenal);
}

/**
 * Estado de albergue de un elemento.
 *
 * @param tipologia      - Tipología canónica (`mapTipologia(tipo, nombre)`).
 * @param nombre         - Nombre normalizado del elemento.
 * @param aforoDeclarado - Aforo que declara el plan, si lo hay (DOP-6).
 */
export function aptitudAlbergue(
  tipologia: TipologiaPTEL | string,
  nombre: string,
  aforoDeclarado?: number | null,
): AptitudAlbergue {
  const res = decidir(tipologia, normalizar(nombre ?? ''));
  if (res.estado !== 'apto' && aforoDeclarado != null && aforoDeclarado > 0) {
    return { ...res, avisos: [...res.avisos, AVISOS_ALBERGUE.planPropone(aforoDeclarado)] };
  }
  return res;
}

/** Motivo y avisos en una sola línea, para exportación y pantalla. */
export function motivoAlbergueCompleto(a: AptitudAlbergue): string {
  return [a.motivo, ...a.avisos].join(' · ');
}
